import { useRef, useEffect } from 'react';
import type React from 'react';
import classNames from 'classnames';

import Badge from 'components/partials/badge/badge';
import Loader from 'components/partials/loader/loader';
import Heading from 'components/partials/heading/heading';
import Tooltip from 'components/partials/tooltip/tooltip';
import { useFeatureHintTarget } from 'components/shared/feature-hints';
import ChatMessageList from './chat-message-list';
import ChatComposer from './chat-composer';
import type { MessageInputHandle } from './message-input';
import NoMessagesContent from './no-messages-content';
import CohortsPopover from './cohorts-popover';
import MarkWalkthroughModal from '../walkthrough/mark-walkthrough-modal';

import { useMarkChat } from '../../hooks/use-mark-chat';
import { useInitialWalkthrough } from '../../hooks/use-initial-walkthrough';
import useExampleMessageSender from '../../hooks/use-example-message-sender';

interface AiChatProps {
  isSidebarOpen: boolean;
  conversationId: string | null;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  registerFocusMessageInput?: (focus: () => void) => void;
  isReadOnly?: boolean;
}

const AiChat = ({
  isSidebarOpen,
  conversationId,
  setSearchParams,
  clientId,
  registerFocusMessageInput,
  isReadOnly,
}: AiChatProps): React.JSX.Element => {
  const messageInputRef = useRef<MessageInputHandle>(null);
  const hintTargetProps = useFeatureHintTarget('chat-input');

  useEffect(() => {
    registerFocusMessageInput?.(() => messageInputRef.current?.focus());
  }, [registerFocusMessageInput]);

  const {
    oktaUserId,
    messages,
    isLoadingConversation,
    isInputDisabled,
    isBlocked,
    limitReachedMessage,
    activeInputPart,
    sendMessage,
    retryMessage,
    regenerateMessage,
    editingMessage,
    startEditingMessage,
    cancelEditingMessage,
    submitEditedMessage,
    submitInputPart,
    dismissInputPart,
    messagesEndRef,
  } = useMarkChat({
    conversationId,
    clientId,
    isReadOnly,
    onConversationCreated: (id) => setSearchParams({ conversationId: id }),
    analyticsSurface: 'markPage',
  });

  const { isOpen: isInitialWalkthroughOpen, close: closeInitialWalkthrough } =
    useInitialWalkthrough(oktaUserId);

  useExampleMessageSender({
    conversationId,
    hasMessages: !!messages.length,
    isReadOnly,
    setSearchParams,
    sendMessage,
  });

  return (
    <div
      className={classNames(
        'relative flex flex-col w-full p-4 min-h-0 max-h-[calc(100vh-8rem)] transition-[margin-left] duration-300 ease-out',
        isSidebarOpen ? 'ml-64' : 'ml-0'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Heading
            variant="h3"
            customClass={classNames(
              'pl-2 py-2 flex-shrink-0 transition-[margin-left] duration-300 ease-out',
              isSidebarOpen ? 'ml-0' : 'ml-[104px]'
            )}
          >
            Ask MARK
          </Heading>
          <Tooltip
            position="bottom-start"
            content={
              <div>
                <p className="font-semibold">MARK is in Public Preview</p>
                <p>
                  A late-stage development release for final testing and tuning.
                  MARK may make mistakes and does not yet include all data that
                  may be available across MARKETview. Please see the Guide & FAQ
                  document for details.
                </p>
              </div>
            }
            customClass="!bg-gray-700 !text-white !px-2 !py-1 max-w-80"
          >
            <Badge text="Public Preview" variant="reserve-outlined" />
          </Tooltip>
        </div>
        <CohortsPopover clientId={clientId} />
      </div>

      {isLoadingConversation && !messages.length ? (
        <Loader loading fullScreen={false} customClass="bg-white" />
      ) : (
        <div
          className={classNames('flex flex-col flex-1 min-h-0 gap-4 w-full', {
            'justify-between': messages.length,
            'justify-center': !messages.length,
          })}
        >
          {!isLoadingConversation && !messages.length && (
            <div className="max-w-[800px] mx-auto w-full">
              <NoMessagesContent
                onPropositionClick={sendMessage}
                isReadOnly={isReadOnly}
                isSendDisabled={isBlocked}
              />
            </div>
          )}

          {!!messages.length && (
            <ChatMessageList
              messages={messages}
              oktaUserId={oktaUserId}
              clientId={clientId}
              conversationId={conversationId}
              isReadOnly={isReadOnly}
              isActionDisabled={isBlocked}
              isEditDisabled={isInputDisabled}
              onRetry={retryMessage}
              onRegenerate={regenerateMessage}
              onEditMessage={startEditingMessage}
              messagesEndRef={messagesEndRef}
              className="mt-9"
            />
          )}

          <ChatComposer
            isBlocked={isBlocked}
            limitReachedMessage={limitReachedMessage}
            activeInputPart={activeInputPart}
            onSubmitInputPart={submitInputPart}
            onDismissInputPart={dismissInputPart}
            onSendMessage={sendMessage}
            isInputDisabled={isInputDisabled}
            editingMessage={editingMessage}
            onSubmitEditedMessage={submitEditedMessage}
            onCancelEditingMessage={cancelEditingMessage}
            inputRef={messageInputRef}
            inputWrapperProps={hintTargetProps}
          />
        </div>
      )}

      {isInitialWalkthroughOpen && (
        <MarkWalkthroughModal
          onClose={closeInitialWalkthrough}
          entryPoint="firstVisit"
        />
      )}
    </div>
  );
};

export default AiChat;
